import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { money, DEMO_MONTH } from "../lib/format";
import { Button } from "../components/ui/button";
import { Plus, Building2, MapPin } from "lucide-react";

export default function Practices() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["practices", DEMO_MONTH],
    queryFn: async () => (await api.get(`/practices?month=${DEMO_MONTH}`)).data,
  });

  return (
    <AppShell>
      <PageHeader
        title="My Practices"
        right={
          <Button data-testid="add-practice-button" onClick={() => navigate("/practices/add")}
            className="rounded-full h-10 w-10 p-0 bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
            <Plus size={20} />
          </Button>
        }
      />
      <div className="px-5 space-y-3 df-fade-in">
        {isLoading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : data?.length ? (
          data.map((p) => (
            <div key={p.id} className="df-card p-5" data-testid={`practice-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-lg text-slate-900 truncate">{p.name}</h3>
                  <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
                    <MapPin size={13} /> {p.suburb || "—"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {p.working_days_labels?.join(" + ") || "No set days"}
                  </div>
                </div>
                <span className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#E6F4F1] text-[#0D786A]">
                  {p.rate_label}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-2">{p.engagement_label} · {p.method_label}</div>

              <div className="grid grid-cols-1 min-[380px]:grid-cols-3 gap-x-2 gap-y-1.5 mt-4 pt-4 border-t border-slate-100">
                <Metric label="Earned" value={money(p.expected_cents)} />
                <Metric label="Received" value={money(p.received_cents)} tone="text-emerald-700" />
                <Metric label="Outstanding" value={money(p.outstanding_cents)} tone="text-indigo-700" />
              </div>

              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button variant="outline" data-testid={`view-practice-${p.id}`} onClick={() => navigate(`/practices/${p.id}`)}
                  className="w-full min-w-0 h-10 rounded-xl text-sm px-2">View Practice</Button>
                <Button variant="outline" data-testid={`edit-agreement-${p.id}`} onClick={() => navigate(`/practices/${p.id}/agreement`)}
                  className="w-full min-w-0 h-10 rounded-xl text-sm px-2">Edit Agreement</Button>
              </div>
            </div>
          ))
        ) : (
          <div className="df-card p-8 text-center">
            <span className="mx-auto h-12 w-12 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center mb-3">
              <Building2 size={22} />
            </span>
            <div className="font-heading font-semibold text-slate-900 text-lg">No practices yet</div>
            <p className="text-sm text-slate-500 mt-1 mb-4">Add a practice and its agreement to start tracking your income.</p>
            <Button data-testid="empty-add-practice" onClick={() => navigate("/practices/add")}
              className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">Add Practice</Button>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Metric({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="flex items-baseline justify-between min-[380px]:block min-w-0">
      <div className="text-[11px] text-slate-400 font-medium shrink-0">{label}</div>
      <div className={`font-num font-semibold text-sm text-right min-[380px]:text-left tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
